// Title: Icon Tile
// Description: Square container that gives icons a consistent surface.

import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "../../lib/utils"

/**
 * CSS variable architecture:
 *
 * The root owns four variables so every part of the tile stays in proportion
 * and stays overridable from a single `className`:
 *
 *   --icon-tile-size       tile width/height
 *   --icon-tile-icon-size  glyph size applied to child svgs
 *   --icon-tile-radius     corner radius (also drives the nested inner card)
 *   --icon-tile-inset      gap between the outer ring and the inner card
 *
 * The `frame` and `soft` variants paint their inner card with an `::after`
 * pseudo element instead of a wrapper node. `isolate` makes the root a stacking
 * context, so the negative z-index pseudo paints above the root background but
 * below the in-flow icon, with no extra DOM.
 *
 * Tone: `soft` derives every fill and border from `currentColor` through
 * `color-mix`, so a single text color class such as `text-success` retints the
 * whole tile. It defaults to `text-v2-primary`; override it to recolor without
 * touching internals.
 */
const iconTileVariants = cva(
  [
    "relative inline-flex shrink-0 items-center justify-center align-middle",
    "size-[var(--icon-tile-size)] rounded-[var(--icon-tile-radius)]",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-[var(--icon-tile-icon-size)]",
  ],
  {
    variants: {
      variant: {
        /** Plain bordered surface. The quiet default for list rows and toolbars. */
        outline: "border border-v2-border bg-v2-background dark:bg-v2-input/[0.32]",
        /** Raised muted fill with a background-colored ring. Reads as a physical chip. */
        elevated:
          "border-2 border-v2-background bg-v2-muted text-v2-accent-foreground shadow-[0_1px_3px_0_rgb(0_0_0/0.14)] dark:border",
        /**
         * Tinted double container: an opacity-filled outer ring with no border
         * around a bordered inner card, all derived from `currentColor`. The
         * quiet, colorful sibling of `frame`. Retint with a text color class.
         */
        soft: [
          "isolate p-[var(--icon-tile-inset)] text-v2-primary bg-[color-mix(in_srgb,currentColor_10%,transparent)]",
          "after:absolute after:-z-10 after:inset-[var(--icon-tile-inset)]",
          "after:rounded-[calc(var(--icon-tile-radius)-var(--icon-tile-inset))]",
          "after:border after:border-[color-mix(in_srgb,currentColor_20%,transparent)] after:bg-[color-mix(in_srgb,currentColor_5%,transparent)]",
        ],
        /** Filled tone with a contrasting glyph. Retint with `bg-*` plus a text color. */
        solid: "bg-v2-primary text-v2-primary-foreground",
        /** Double container: a muted ring around an inset card, matching `soft`. */
        frame: [
          "isolate border border-v2-border bg-v2-muted p-[var(--icon-tile-inset)]",
          "after:absolute after:-z-10 after:inset-[var(--icon-tile-inset)]",
          "after:rounded-[calc(var(--icon-tile-radius)-var(--icon-tile-inset))]",
          "after:border after:border-v2-border after:bg-v2-background after:shadow-sm",
        ],
      },
      size: {
        xs: "[--icon-tile-size:1.5rem] [--icon-tile-icon-size:0.875rem] [--icon-tile-inset:0.125rem]",
        sm: "[--icon-tile-size:2rem] [--icon-tile-icon-size:1rem] [--icon-tile-inset:0.125rem]",
        default:
          "[--icon-tile-size:2.5rem] [--icon-tile-icon-size:1.125rem] [--icon-tile-inset:0.125rem]",
        lg: "[--icon-tile-size:3rem] [--icon-tile-icon-size:1.375rem] [--icon-tile-inset:0.125rem]",
        xl: "[--icon-tile-size:3.5rem] [--icon-tile-icon-size:1.75rem] [--icon-tile-inset:0.25rem]",
      },
      /** `default`: the theme radius. `full`: circular. */
      radius: {
        default: "[--icon-tile-radius:var(--v2-radius)]",
        full: "[--icon-tile-radius:calc(infinity*1px)]",
      },
    },
    /**
     * `frame` is the default because the double container is the house look for
     * a framed icon — Apps & Extensions, the connection detail panes, and
     * {@link EmptyStateIcon} all reach for it explicitly, which is the tell that
     * the plain `outline` default was the odd one out. `outline` remains the
     * right choice for a dense list row or toolbar, where the inner card reads
     * as clutter, but it now has to be asked for.
     */
    defaultVariants: {
      variant: "frame",
      size: "default",
      radius: "default",
    },
  }
)

export interface IconTileProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof iconTileVariants> {
  /** Render as the single child element instead of a `span`. */
  asChild?: boolean
}

/**
 * Square surface that frames an icon at a consistent size and tone.
 *
 * @param props - Variant, size, radius, and standard span props. Pass `asChild`
 * to merge the styling onto the child element.
 *
 * @returns The icon tile element.
 */
const IconTile = React.forwardRef<HTMLSpanElement, IconTileProps>(
  ({ className, variant, size, radius, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "span"

    return (
      <Comp
        ref={ref}
        data-slot="icon-tile"
        data-variant={variant ?? "frame"}
        data-size={size ?? "default"}
        className={cn(iconTileVariants({ variant, size, radius }), className)}
        {...props}
      />
    )
  }
)
IconTile.displayName = "IconTile"

export { IconTile, iconTileVariants }
